import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WizardShell, { WizardStatus } from '../../components/WizardShell';
import QuestionField, { isAnswered, isVisible } from '../../components/QuestionField';
import Callout from '../../components/Callout';
import { COMMON_QUESTIONS, getVertical, type Question } from '../../data/verticals';
import { useProject } from '../../lib/useProject';

/** Three or four questions per screen: enough to feel like progress, few
 *  enough to fit on a phone without scrolling past the Continue button. */
const PER_SCREEN = 3;

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export default function Questions() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { project, loading, loadError, saveState, setAnswer, flush } = useProject(id);
  const [screen, setScreen] = useState(0);
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);

  const vertical = project?.verticalId ? getVertical(project.verticalId) : undefined;
  const answers = project?.answers ?? {};

  /**
   * Conditional questions are filtered before paging, so a hidden question
   * never leaves a gap and never strands a screen with nothing on it.
   */
  const screens = useMemo(() => {
    if (!vertical) return [] as Question[][];
    const all = [...vertical.questions, ...COMMON_QUESTIONS];
    return chunk(
      all.filter((q) => isVisible(q, answers)),
      PER_SCREEN,
    );
  }, [vertical, answers]);

  if (loading) return <WizardStatus message="Loading your project…" />;
  if (loadError) return <WizardStatus message={loadError} />;
  if (!project || !vertical) {
    return <WizardStatus message="We could not find that project." />;
  }

  const index = Math.min(screen, Math.max(screens.length - 1, 0));
  const current = screens[index] ?? [];
  const isLast = index >= screens.length - 1;

  const missing = current.filter((q) => q.required && !isAnswered(answers[q.id]));

  const go = async (next: number) => {
    setBusy(true);
    await flush();
    setBusy(false);
    setShowErrors(false);
    setScreen(next);
    window.scrollTo({ top: 0 });
  };

  const onContinue = async () => {
    if (missing.length > 0) {
      setShowErrors(true);
      return;
    }
    if (isLast) {
      setBusy(true);
      await flush();
      setBusy(false);
      navigate(`/project/${project.id}/size`);
      return;
    }
    await go(index + 1);
  };

  const onBack = () => {
    if (index === 0) {
      navigate('/new');
      return;
    }
    void go(index - 1);
  };

  return (
    <WizardShell
      step={1}
      verticalLabel={vertical.label}
      title="Tell us about the building"
      intro={
        index === 0
          ? 'A rough answer is better than no answer. You can change any of this later, and nothing here is a commitment.'
          : undefined
      }
      saveState={saveState}
      onBack={onBack}
      backLabel={index === 0 ? 'Pick a different building type' : 'Go back'}
      onContinue={() => void onContinue()}
      continueLabel={isLast ? 'See the size' : 'Continue'}
      busy={busy}
    >
      <p className="t-meta text-grey-600">
        Screen <span className="t-numeral">{index + 1}</span> of{' '}
        <span className="t-numeral">{screens.length}</span>
      </p>

      <div className="mt-6 flex flex-col gap-10">
        {current.map((q) => (
          <QuestionField
            key={q.id}
            question={q}
            value={answers[q.id]}
            onChange={(v) => setAnswer(q.id, v)}
            error={
              showErrors && q.required && !isAnswered(answers[q.id])
                ? 'We need this one to size your building.'
                : undefined
            }
          />
        ))}
      </div>

      {showErrors && missing.length > 0 && (
        <div className="mt-8">
          <Callout tone="caution" title="A couple still need answers">
            {missing.length === 1
              ? 'One question above is still blank.'
              : `${missing.length} questions above are still blank.`}{' '}
            An estimate is fine — we only need to be in the right range.
          </Callout>
        </div>
      )}
    </WizardShell>
  );
}
